/**
 * LanguageWelcomeModal
 * ====================
 * One-time modal shown the very first time a user lands on the app. Forces
 * a deliberate language choice (ES / EN) so users immediately see the UI in
 * their preferred locale instead of having to hunt for a switcher buried in
 * the dashboard sidebar.
 *
 * Trigger logic
 * -------------
 * Renders only when `localStorage.getItem('ofimex_locale')` is null. As soon
 * as the user picks a language, `changeLocale` writes that key, so this
 * modal never re-appears on future visits (unless the user clears their
 * browser storage).
 *
 * Mounted at the root of <AppRoutes> so it overlays any public or
 * authenticated page on first visit.
 */
import React, { useEffect, useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Globe } from 'lucide-react';

const STORAGE_KEY = 'ofimex_locale';

export function LanguageWelcomeModal() {
  const { changeLocale } = useI18n();
  // Default to hidden — we set true in effect after checking storage. This
  // avoids a flash of the modal on every render before we know the state.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setOpen(true);
      }
    } catch { /* localStorage blocked → just skip */ }
  }, []);

  if (!open) return null;

  const choose = (code) => {
    changeLocale(code);  // also writes to localStorage
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lang-welcome-title"
      data-testid="language-welcome-modal"
    >
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-3">
            <Globe size={28} className="text-primary" />
          </div>
          <h2 id="lang-welcome-title" className="text-2xl font-heading font-bold">
            Elige tu idioma
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose your language · Puedes cambiarlo después en cualquier momento
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => choose('es')}
            className="group flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
            data-testid="lang-welcome-es"
            autoFocus
          >
            <span className="text-4xl group-hover:scale-110 transition-transform">🇲🇽</span>
            <span className="font-semibold text-base">Español</span>
            <span className="text-xs text-muted-foreground">Spanish</span>
          </button>

          <button
            onClick={() => choose('en')}
            className="group flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
            data-testid="lang-welcome-en"
          >
            <span className="text-4xl group-hover:scale-110 transition-transform">🇺🇸</span>
            <span className="font-semibold text-base">English</span>
            <span className="text-xs text-muted-foreground">Inglés</span>
          </button>
        </div>

        <p className="text-[11px] text-center text-muted-foreground mt-6">
          OFIMEX · Sistema de gestión de importaciones
        </p>
      </div>
    </div>
  );
}
